/**
 * NotFoundPage — 404 with correct semantics + brand-consistent styling.
 * Returns HTTP 404 via a <meta http-equiv> tag as a fallback for crawlers
 * (real 404 headers require server-side routing which isn't in scope here).
 */
import { Link } from "react-router-dom";
import { ArrowLeft, HomeIcon, Sparkles } from "lucide-react";
import { Header } from "../components/Header";
import { SocialMeta } from "../components/SocialMeta";
import { SiteFooter } from "../components/SiteFooter";
import { VAULT } from "../data/vaultStats";

export default function NotFoundPage() {
  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="not-found-page">
      <SocialMeta
        title="404 · Page not found · Stag Vault"
        description={`Sorry — that page doesn't exist. Head back to the Stag Vault homepage or browse the ${VAULT.promptsFmt}-prompt vault.`}
        noindex
      />
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-24">
        <div className="max-w-xl text-center">
          <div className="inline-flex items-center gap-2 text-purple-400 text-xs tracking-[0.35em] uppercase mb-6 font-mono">
            <Sparkles size={12} strokeWidth={2.5} /> 404 · Not found
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6">
            This page ran off with the herd.
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed mb-10">
            Whatever URL brought you here doesn&apos;t exist. Head back to the vault — {VAULT.tracks} income tracks and {VAULT.promptsFmt} prompts are waiting.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-3 bg-purple-500 hover:bg-purple-400 text-black px-6 py-3.5 text-xs tracking-[0.25em] uppercase font-bold transition-all"
              data-testid="not-found-home-cta"
            >
              <HomeIcon size={14} strokeWidth={2.5} /> Back to home
            </Link>
            <Link
              to="/library-preview"
              className="inline-flex items-center gap-3 text-purple-300 hover:text-purple-200 text-xs tracking-[0.25em] uppercase transition-colors"
            >
              <ArrowLeft size={14} /> Try 5 prompts free
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
